import React, { useState, useEffect, useRef } from 'react';
import './ActionCell.scss';

/**
 * ActionsCell Component
 * 
 * A component that displays an action menu with conditional menu items based on application type and progress.
 * 
 * @component
 * @memberof Components
 * @param {Object} props - Component props.
 * @param {Object} props.data - Data object containing application details and action functions.
 * @param {boolean} props.data.releventApplicationFlage - Indicates if the application is relevant to user.
 * @param {boolean} props.data.exceededDuration - Determines the color of the action button for normal and overdue.
 * @param {boolean} props.data.downloadDNPDF - Function to download Demand Note PDF.
 * @param {boolean} props.data.downloadSCOPDF - Function to download SCO PDF.
 * @param {boolean} props.data.downloadEFile - Function to download E-File.
 * @param {number} props.data.applicationType - Type of application (used for menu filtering).
 * @param {number} props.data.applicationProgressNumber - Progress level of the application.
 * @param {Function} props.data.downloadBill - Function to download the bill (if applicable).
 * @param {Function} props.data.downloadVoucherPDF - Function to download a voucher PDF (if applicable).
 * @param {Function} props.data.downloadRCOPDF - Function to download RCO PDF (if applicable).
 * @returns {JSX.Element} The rendered ActionsCell component.
 */
const ActionsCell = ({ data }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    /**
     * Toggles the menu open/close state.
     * @param {React.MouseEvent} e - Click event.
     */
    const onViewMenu = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        setMenuOpen(!menuOpen);
    };

    /**
    * Closes the menu if a click occurs outside the menu.
    * @param {MouseEvent} e - Click event.
    */
    const handleClickOutside = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
            setMenuOpen(false); // Close menu if click is outside the menu
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

     useEffect(() => {
        if(data){
            // console.log("data in action cell",data)
        }
    }, [data]);

    return (
        <div style={{ position: 'absolute', margin: -12, marginLeft: 22, padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
            {
                data.releventApplicationFlage ?
                    <div class="blinking-circle" style={{ marginLeft: !(data.downloadDNPDF || data.downloadEFile) ? '45px' : '' }}>
                        <span className='hover-text'>
                            Needs Action
                        </span>
                    </div> :
                    <div className='empty-circle'></div>
            }

            {
                (data.downloadDNPDF || data.downloadEFile) &&
                <div
                    style={{
                        backgroundColor: data.exceededDuration ? '#9d192f' : '#006ad1',
                        color: 'white',
                        borderRadius: '5px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '25px',
                        height: '25px',
                        cursor: 'pointer',
                    }}
                    onClick={onViewMenu}
                >
                    ...
                </div>
            }

            {menuOpen && (
                <>
                    {
                        data.applicationType == 1 &&
                        <>
                            <div
                                ref={menuRef}
                                style={{
                                    position: 'absolute',
                                    left: '-100px', // Adjust this value to position the menu to the left of the button
                                    top: '20px', // Adjust this value to position the menu below the button
                                    backgroundColor: 'white',
                                    boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
                                    borderRadius: '10px',
                                    textAlign: 'left',
                                    padding: '10px',
                                    zIndex: 1001, // Ensure menu appears above other content
                                    cursor: 'pointer',
                                    minWidth: '100px', // Adjust as needed for menu width
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: 'column'
                                }}
                            >
                                {
                                    data.applicationProgressNumber >= 5 &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadDNPDF(data.applicationId)
                                    }}>Demand Note</div>
                                }
                                {
                                    data.applicationProgressNumber >= 7 &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadSCOPDF(data.applicationId)
                                    }}>SCO</div>
                                }
                                <div className='menu-item' onClick={async (e) => {
                                    e.stopPropagation();
                                    await data.downloadEFile(data.applicationId)
                                }}>E-File</div>
                                 {
                                    (data.applicationProgressNumber > 7 && data.appliedLoad < 5) &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadNOCPDF(data.applicationId)
                                    }}>NOC</div>
                                }
                                {
                                    (data.applicationProgressNumber >= 4 && data.appliedLoad > 4 && data.isLTLWI40Flag == 0) &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadEstimatePDF(data.applicationId)
                                    }}>Cost Estimate</div>
                                }
                            </div>
                        </>
                    }
                    {
                        data.applicationType == 13 &&
                        <>
                            <div
                                ref={menuRef}
                                style={{
                                    position: 'absolute',
                                    left: '-100px', // Adjust this value to position the menu to the left of the button
                                    top: '20px', // Adjust this value to position the menu below the button
                                    backgroundColor: 'white',
                                    boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
                                    borderRadius: '10px',
                                    textAlign: 'left',
                                    padding: '10px',
                                    zIndex: 1001, // Ensure menu appears above other content
                                    cursor: 'pointer',
                                    minWidth: '100px', // Adjust as needed for menu width
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: 'column'
                                }}
                            >
                                {/* {
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadBill()
                                    }}>Bill</div>
                                } */}
                                {/* {
                                    data.applicationProgressNumber >= 7 &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadRCOPDF()
                                    }}>RCO</div>
                                } */}
                                <div className='menu-item' onClick={async (e) => {
                                    e.stopPropagation();
                                    await data.downloadEFile(data.applicationId)
                                }}>E-File</div>
                                {
                                    (data.applicationProgressNumber >= 7 && data.isDiscAboveAYear === true) &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadDNPDF(data.applicationId)
                                    }}>Demand Note</div>
                                }
                                 {
                                    (data.applicationProgressNumber == 3 || data.applicationProgressNumber == 4) &&
                                    <>
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadRCOFeePDF()
                                    }}>RCO Fee Voucher</div>
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadFixedChargesPDF()
                                    }}>Fixed Charges Voucher</div>
                                    </>
                                }
                                {
                                    data.applicationProgressNumber == 12 &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadCP138PDF()
                                    }}>CP-138</div>
                                }
                            </div>
                        </>
                    }
                    {
                        data.applicationType == 15 &&
                        <>
                            <div
                                ref={menuRef}
                                style={{
                                    position: 'absolute',
                                    left: '-100px', // Adjust this value to position the menu to the left of the button
                                    top: '20px', // Adjust this value to position the menu below the button
                                    backgroundColor: 'white',
                                    boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
                                    borderRadius: '10px',
                                    textAlign: 'left',
                                    padding: '10px',
                                    zIndex: 1001, // Ensure menu appears above other content
                                    cursor: 'pointer',
                                    minWidth: '100px', // Adjust as needed for menu width
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: 'column'
                                }}
                            >
                                {
                                    data.applicationProgressId >= 5 &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadDNPDF(data.applicationId)
                                    }}>Demand Note</div>
                                }
                                <div className='menu-item' onClick={async (e) => {
                                    e.stopPropagation();
                                    await data.downloadEFile(data.applicationId)
                                }}>E-File</div>
                               
                            </div>
                        </>
                    }
                     {
                        data.applicationType == 2 &&
                        <>
                            <div
                                ref={menuRef}
                                style={{
                                    position: 'absolute',
                                    left: '-100px', // Adjust this value to position the menu to the left of the button
                                    top: '20px', // Adjust this value to position the menu below the button
                                    backgroundColor: 'white',
                                    boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
                                    borderRadius: '10px',
                                    textAlign: 'left',
                                    padding: '10px',
                                    zIndex: 1001, // Ensure menu appears above other content
                                    cursor: 'pointer',
                                    minWidth: '100px', // Adjust as needed for menu width
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: 'column'
                                }}
                            >
                                {
                                    data.applicationProgressNumber >= 6 && data.isMCOWithCost === true && data.isMeterAvailable === true &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadDNPDF(data.applicationId)
                                    }}>Demand Note</div>
                                }
                                {
                                     data.applicationProgressNumber >= 11 && 
                                     <div className='menu-item' onClick={async (e) => {
                                    e.stopPropagation();
                                    await data.downloadCP138PDF(data.applicationId)
                                }}>CP-138</div>
                                }
                                
                                <div className='menu-item' onClick={async (e) => {
                                    e.stopPropagation();
                                    await data.downloadEFile(data.applicationId)
                                }}>E-File</div>
                                 {
                                    (data.applicationProgressNumber >= 7 && data.sanctionedLoad < 5) &&
                                    <div className='menu-item' onClick={async (e) => {
                                        e.stopPropagation();
                                        await data.downloadNOCPDF(data)
                                    }}>NOC</div>
                                }
                            </div>
                        </>
                    }
                </>
            )}
        </div>
    );
};

export default ActionsCell;